<script lang="ts">
  /**
   * The Landing Hub: the front page a timed-out session lands on.
   *
   * Reachability is not the hub's concern — the container mounts it only from
   * a fired wake check or a persisted `TimedOut` cell (D2), and never on a
   * fresh launch. What the hub owns is the presentation:
   *
   * - the notebook's name as a READ-ONLY label (D3 — this phase has exactly
   *   one notebook, so a selector would be a component with nothing to do);
   * - the cycle boundary (null renders as an em dash);
   * - `[ Resume Session at Page N ]` — primary, N interpolated off the
   *   session's recorded page (D5); rendered ONLY when there is a session
   *   to resume — a placeholder cell (lamport 1: this device never wrote a
   *   session for the notebook) hides it, leaving the front-door hub with
   *   Restart alone;
   * - `[ Restart Cycle at Page N ]` — secondary, N the first active page
   *   (D4 as amended: the label names the page the restart lands on);
   * - the checkpoint banner above the exits when there is a live task to
   *   resolve, or the degraded note when there is not (D7 cases 2–3).
   *
   * The exits carry `aria-disabled` — never `disabled`, which would skip them
   * by screen readers entirely and hide the gate the lifecycle diagram puts
   * between resolution and resume (D5). While gated, a click is also inert:
   * the label promises what the button DOES, so activate() checks before it
   * calls, the same pattern as PageFooter.
   *
   * `checkpointPageNumber` is this spec's one contract extension (ledger
   * ruling): the banner must name the task's OWN page — self-consistent even
   * when the session's page has drifted (D7 case 1) — and the task does not
   * carry a page number, only a page id.
   */
  import Icon from './Icon.svelte';
  import ArrowRight from 'lucide/dist/esm/icons/arrow-right.mjs';
  import RotateCcw from 'lucide/dist/esm/icons/rotate-ccw.mjs';
  import type { Task } from '../types/generated';
  import ResumeCheckpointBanner from './ResumeCheckpointBanner.svelte';

  interface Props {
    notebookName: string;
    /** Null renders as an em dash. */
    cycleBoundary: number | null;
    /** For the Resume label — the session's recorded page; null hides Resume. */
    resumePageNumber: number | null;
    /** For the Restart label — the first active page, where Restart goes. */
    restartPageNumber: number;
    /** Null = degraded rendering (D7 cases 2–3). */
    checkpointTask: Task | null;
    /** The checkpoint task's own page, for the banner copy. */
    checkpointPageNumber: number;
    /** Degraded reason line; null when live. */
    checkpointNote: string | null;
    /** Gates the exits (D5). */
    checkpointResolved: boolean;
    onResume: () => void;
    onRestartCycle: () => void;
    onFinished: () => void | Promise<void>;
    onReenter: () => void;
    onLeaveActive: () => void;
  }

  let {
    notebookName,
    cycleBoundary,
    resumePageNumber,
    restartPageNumber,
    checkpointTask,
    checkpointPageNumber,
    checkpointNote,
    checkpointResolved,
    onResume,
    onRestartCycle,
    onFinished,
    onReenter,
    onLeaveActive,
  }: Props = $props();

  function activate(exit: 'resume' | 'restart') {
    if (!checkpointResolved) return;
    if (exit === 'resume') onResume();
    else onRestartCycle();
  }

  /**
   * The hub takes focus on open (idea 14). The stage now stays mounted behind
   * it — `inert`, so still nothing else to tab into — and without this a
   * keyboard user could land on `<body>` with the caret nowhere: the same bug
   * class the takeover and the banner heading were ruled on for. The root is
   * `tabindex="-1"`: the dialog pattern's programmatic focus, not a tab stop.
   */
  let root = $state<HTMLElement | null>(null);
  $effect(() => {
    root?.focus();
  });
</script>

<div class="landing-hub" data-testid="landing-hub" tabindex="-1" bind:this={root}>
  <p class="hub-notebook" data-testid="hub-notebook">{notebookName}</p>

  <p class="hub-boundary">
    Cycle boundary: <span data-testid="hub-cycle-boundary">{cycleBoundary ?? '—'}</span>
  </p>

  {#if checkpointTask}
    <ResumeCheckpointBanner
      task={checkpointTask}
      pageNumber={checkpointPageNumber}
      onFinished={onFinished}
      onReenter={onReenter}
      onLeaveActive={onLeaveActive}
    />
  {:else if checkpointNote}
    <p class="hub-checkpoint-note" data-testid="checkpoint-note">{checkpointNote}</p>
  {/if}

  <div class="hub-exits">
    {#if resumePageNumber !== null}
      <button
        class="action"
        type="button"
        data-testid="landing-hub-resume"
        aria-disabled={!checkpointResolved}
        onclick={() => activate('resume')}
      >
        <Icon node={ArrowRight} />
        [ Resume Session at Page {resumePageNumber} ]
      </button>
    {/if}
    <button
      class="action"
      type="button"
      data-testid="landing-hub-restart"
      aria-disabled={!checkpointResolved}
      onclick={() => activate('restart')}
    >
      <Icon node={RotateCcw} />
      [ Restart Cycle at Page {restartPageNumber} ]
    </button>
  </div>
</div>

<style>
  .landing-hub {
    display: flex;
    flex-direction: column;
    gap: 14px;
    width: min(100%, 34em);
    padding: 28px 24px;
    font-family: var(--font-ui);
    font-size: 13px;
    color: var(--ink);
    /* A paper card floating over the veiled page (idea 14): the hub-host
       centres it; the card carries the surface. */
    background: var(--paper);
    border-radius: 2px;
    box-shadow: var(--shadow-card);
  }

  .hub-notebook {
    margin: 0;
    font-size: 18px;
    font-family: var(--font-hand, var(--font-ui));
  }

  .hub-boundary {
    margin: 0;
    color: var(--ink-muted);
  }

  .hub-checkpoint-note {
    margin: 0;
    padding: 12px 14px;
    border: 1px solid color-mix(in srgb, var(--ink) 16%, transparent);
    border-radius: 4px;
    /* `var(--paper)`, not the `#fdfcf7` literal it replaced: identical in light,
       and it flips to the chalkboard surface in dark. */
    background: var(--paper);
    box-shadow: var(--shadow-card);
  }

  .hub-exits {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
</style>
